import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { graphql } from '@/gql';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormLayout';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { useBlobPreview } from '@/lib/objectUrl';

const CreatePosterUploadUrlDoc = graphql(/* GraphQL */ `
  mutation CreatePosterUploadUrl($contentType: String!) {
    createPosterUploadUrl(contentType: $contentType) {
      uploadUrl
      posterPath
      posterUrl
    }
  }
`);

const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

function isPosterNotConfigured(message: string): boolean {
  return /poster uploads are not configured|GCS_POSTER_BUCKET|signBlob|sign URL/i.test(
    message,
  );
}

/**
 * Poster upload (loyalty.md 5.1 / 5.3). Requests a V4 signed PUT, uploads the
 * file to GCS, then persists the returned `posterPath`. When
 * `GCS_POSTER_BUCKET` is empty the mutation fails with `ErrPosterNotConfigured`
 * — show that honestly and keep a paste-a-path fallback so the form stays usable.
 */
export function PosterField({
  posterUrl,
  posterPath,
  onPathChange,
}: {
  posterUrl?: string | null;
  posterPath: string;
  onPathChange: (path: string) => void;
}) {
  const [createUploadUrl] = useMutation(CreatePosterUploadUrlDoc);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [bucketMissing, setBucketMissing] = useState(false);
  const [remotePreview, setRemotePreview] = useState<string | null>(null);
  const [blobFile, setBlobFile] = useState<File | null>(null);
  const blobPreview = useBlobPreview(blobFile);

  const preview = blobPreview || remotePreview || posterUrl || null;

  const handleFile = async (file: File) => {
    setUploadError(null);
    if (!ACCEPTED_TYPES.has(file.type)) {
      setUploadError('Use a JPEG, PNG, WebP or GIF image.');
      return;
    }
    setUploading(true);
    try {
      const result = await createUploadUrl({
        variables: { contentType: file.type },
      });
      const payload = result.data?.createPosterUploadUrl;
      if (!payload) {
        throw new Error('No upload URL returned.');
      }
      const put = await fetch(payload.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!put.ok) {
        throw new Error(`Upload to storage failed (${put.status}).`);
      }
      onPathChange(payload.posterPath);
      if (payload.posterUrl) {
        setBlobFile(null);
        setRemotePreview(payload.posterUrl);
      } else {
        setRemotePreview(null);
        setBlobFile(file);
      }
      setBucketMissing(false);
    } catch (err) {
      const msg = graphqlErrorMessage(err, 'Could not upload the poster.');
      if (isPosterNotConfigured(msg)) {
        setBucketMissing(true);
        setUploadError(
          'Poster uploads are not configured on this API (GCS_POSTER_BUCKET is empty, or the service account cannot sign URLs). Paste a GCS object path below to save the form, or set the bucket locally — do not invent credentials.',
        );
      } else {
        setUploadError(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <FormField
      label="Poster"
      description={
        bucketMissing
          ? 'Signed upload is unavailable until GCS_POSTER_BUCKET and a signing service account are set. You can still paste an existing object path.'
          : '3:4 portrait, 1200×1600 recommended. Used on the website and the app. JPEG, PNG, WebP or GIF. The browser PUTs the file to a 15-minute signed URL; Go never sees the bytes.'
      }
      error={uploadError}
    >
      {preview ? (
        <img
          src={preview}
          alt=""
          className="mb-3 h-32 w-auto rounded-lg border border-gray-200 object-cover dark:border-white/10"
        />
      ) : null}
      <label className="mb-3 inline-flex cursor-pointer items-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="sr-only"
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void handleFile(file);
          }}
        />
        {uploading ? 'Uploading…' : 'Upload image'}
      </label>
      <Input
        value={posterPath}
        onChange={(e) => onPathChange(e.target.value)}
        placeholder="posters/…"
        aria-label="Poster object path"
      />
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Path fallback (e.g. posters/messy.jpg) for when signed upload is not configured.
      </p>
    </FormField>
  );
}
